import { useState, useEffect, useRef } from 'react';
import Papa from 'papaparse';
import LoadingSpinner from './LoadingSpinner';
import CardsList from './CardsList';
import DetailedView from './DetailedView';
import { useLanguage } from '../context/LanguageContext';
import './CsvViewer.css';
import { getBrandLogoFilename } from '../brandMapping';
import HeroSlideshow from './HeroSlideshow';
import { getProductIcon } from '../productIconMapping';
import { shouldShowTitle, titleFilterConfig } from '../data/titleFilterConfig';
import { formatAandeelDisplay } from '../utils/aandeelDisplay';
import { getCsvUrl } from '../utils/csvUrl';

// 1x1 transparent GIF – used when icon and placeholder both fail to stop further load attempts
const IMAGE_LOAD_STOP_DATA_URI = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const CustomSelect = ({ value, onChange, options, disabled, placeholder, className, t }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOptionClick = (option) => {
    if (option.disabled) return;
    
    setSelectedValue(option.value);
    onChange({ target: { value: option.value } });
    setIsOpen(false);
  };

  const filterKeys = ['Mediumtype', 'Titel', 'Aandeel', 'Formaat', 'Positie'];

  const getDisplayValue = () => {
    if (!selectedValue && placeholder) return placeholder;
    const selected = options.find(option => option.value === selectedValue);
    if (selected) {
      // If the selected value is a filter key, use translation
      if (filterKeys.includes(selected.value)) {
        return t(`filterLabel${selected.value}`, selected.label);
      }
      return selected.label;
    }
    return placeholder;
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div 
      className={`custom-select-container ${disabled ? 'disabled' : ''} ${className || ''}`} 
      ref={dropdownRef}
    >
      <div 
        className={`custom-select-header ${isOpen ? 'open' : ''} ${selectedValue ? 'has-value' : ''}`}
        onClick={toggleDropdown}
      >
        <span className="custom-select-value">{getDisplayValue()}</span>
        <span className="custom-select-arrow"></span>
      </div>
      
      {isOpen && (
        <div className="custom-select-dropdown">
          {options.map((option, index) => (
            <div 
              key={index}
              className={`custom-select-option ${option.isHeader ? 'filter-option-header' : ''} ${option.className || ''} ${option.value === selectedValue ? 'selected' : ''} ${option.disabled ? 'disabled' : ''}`}
              onClick={() => handleOptionClick(option)}
              data-header={option.isHeader ? option.label : undefined}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
      
      {/* Hidden native select for form submission */}
      <select 
        value={selectedValue || ''} 
        onChange={onChange} 
        disabled={disabled}
        style={{ display: 'none' }}
      >
        {options.map((option, index) => (
          <option key={index} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

const CsvViewer = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    Mediumtype: '',
    Titel: '',
    Aandeel: '',
    Formaat: '',
    Positie: '',
    Provincie: '',
    Zipcode: ''
  });
  const [contentType, setContentType] = useState('advertising');
  const [selectedRow, setSelectedRow] = useState(null);
  const [isFiltering, setIsFiltering] = useState(false);
  const [selectionMode, setSelectionMode] = useState('filter'); // 'filter', 'step-by-step', or 'region'
  const [currentStep, setCurrentStep] = useState(1); // For step-by-step selection: 1=Mediumtype, 2=Titel, 3=Aandeel, 4=Position
  const [regionStep, setRegionStep] = useState(1); // For region search: 1=Province, 2=Title, 3=Format
  const [showFilterNotification, setShowFilterNotification] = useState(false);
  const [zipcodeTitleMap, setZipcodeTitleMap] = useState({});
  const [isLoadingZipcodes, setIsLoadingZipcodes] = useState(false);
  const [zipcodeLocationData, setZipcodeLocationData] = useState({});
  const [zipcodeLocation, setZipcodeLocation] = useState(null);
  const [isLoadingLocationData, setIsLoadingLocationData] = useState(false);
  const { t } = useLanguage();

  // Print medium types (excludes Nieuwsbrief, which is Digital/Online and uses Format & Position)
  const isPrintMediumType = (mediumtype) => {
    if (!mediumtype) return false;
    const mt = (mediumtype || '').trim();
    return mt.startsWith('Dagblad') || ['Magazine', 'Nieuwsblad', 'Weekblad', 'Special', 'Weekkrant', 'Nieuws- en weekbladen', 'Print (totaal)', 'Mediahuis Connect'].includes(mt);
  };

  // Load city_zipcodes.csv data for location lookup
  useEffect(() => {
    const loadZipcodeLocationData = async () => {
      try {
        setIsLoadingLocationData(true);
        const response = await fetch(`${process.env.PUBLIC_URL}/assets/city_zipcodes.csv`);
        const csvText = await response.text();
        
        // Check if file loaded successfully
        console.log(`CSV file loaded, length: ${csvText.length} characters`);
        console.log(`First 100 characters: ${csvText.substring(0, 100)}`);
        
        // Manual parsing of CSV to ensure it works correctly
        const zipcodeData = {};
        const lines = csvText.split('\n');
        
        console.log(`CSV has ${lines.length} lines`);
        console.log(`Header line: ${lines[0]}`);
        console.log(`Sample lines:`, lines.slice(1, 3));
        
        // Skip the header line (first line)
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;  // Skip empty lines
          
          // Handle potential quoted fields
          let parts;
          if (line.includes('"')) {
            // More complex parsing for lines with quotes
            // For now just split by comma as a simplified approach
            parts = line.split(',');
          } else {
            parts = line.split(',');
          }
          
          if (parts.length >= 4) {
            const zipcode = parts[0].trim();
            const city = parts[1].trim();
            const municipality = parts[2].trim();
            const province = parts[3].trim();
            
            if (zipcode && city && municipality && province) {
              zipcodeData[zipcode] = {
                city,
                municipality,
                province
              };
              
              // Removed logging for specific zipcode
            }
          }
        }
        
        
        
        setZipcodeLocationData(zipcodeData);
        setIsLoadingLocationData(false);
      } catch (error) {
        console.error('Error loading city zipcode CSV:', error);
        setIsLoadingLocationData(false);
      }
    };

    loadZipcodeLocationData();
  }, []);

  // Load brands_zipcodes.csv data
  useEffect(() => {
    const loadZipcodeData = async () => {
      try {
        setIsLoadingZipcodes(true);
        const response = await fetch(`${process.env.PUBLIC_URL}/assets/brands_zipcodes.csv`);
        const csvText = await response.text();
        
        const zipcodesToTitles = {};
        
        Papa.parse(csvText, {
          delimiter: ';',
          complete: (results) => {
            results.data.forEach(row => {
              if (row.length >= 2) { // Ensure row has title and zipcodes
                const title = row[0];
                const zipcodes = row[1].split(', ').map(zip => zip.trim());
                
                zipcodes.forEach(zipcode => {
                  if (!zipcodesToTitles[zipcode]) {
                    zipcodesToTitles[zipcode] = [];
                  }
                  zipcodesToTitles[zipcode].push(title);
                });
              }
            });
            
            setZipcodeTitleMap(zipcodesToTitles);
            setIsLoadingZipcodes(false);
          },
          error: (error) => {
            console.error('Error parsing zipcode CSV:', error);
            setIsLoadingZipcodes(false);
          }
        });
      } catch (error) {
        console.error('Error loading zipcode CSV:', error);
        setIsLoadingZipcodes(false);
      }
    };

    loadZipcodeData();
  }, []);

  // Load CSV data only once
  useEffect(() => {
    const loadData = async () => {
      try {
        const csvUrl = getCsvUrl();
        const response = await fetch(csvUrl);
        const csvText = await response.text();
        
        Papa.parse(csvText, {
          header: true,
          complete: (results) => {
            const parsedData = results.data;
            setData(parsedData);
            if (parsedData.length > 0) {
              setColumns(Object.keys(parsedData[0]));
            }
            setIsLoading(false);
          },
          error: (error) => {
            console.error('Error parsing CSV:', error);
            setIsLoading(false);
          }
        });
      } catch (error) {
        console.error('Error loading CSV:', error);
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Filter data when filters or data changes
  useEffect(() => {
    const applyFilters = async () => {
      // Early return if data is not loaded yet
      if (!data || data.length === 0) {
        setFilteredData([]);
        return;
      }
      
      if ((selectionMode === 'filter' && filters.Mediumtype && filters.Titel) || 
          (selectionMode === 'step-by-step' && currentStep > 2) ||
          (selectionMode === 'region' && ((regionStep === 1 && filters.Provincie) || 
                                         (regionStep === 1 && filters.Zipcode && filters.Zipcode.length === 4) ||
                                         (regionStep === 2 && (filters.Provincie || (filters.Zipcode && filters.Zipcode.length === 4)) && filters.Titel)))) {
        setIsFiltering(true);
        // Add small delay to show loading state even for fast operations
        await new Promise(resolve => setTimeout(resolve, 300));
        
        let filtered = [];
        
        // Apply different filtering logic based on selection mode
        if (selectionMode === 'region') {
          // For region search, only include print media
          filtered = data.filter(item => {
            // Filter for print media types only
            const mt = (item.Mediumtype || '').trim();
            const isPrintMedia = mt.startsWith('Dagblad') || ['Magazine','Nieuwsblad','Weekblad','Print (totaal)','Special','Weekkrant'].includes(mt);
            if (!isPrintMedia) return false;
            
            // If zipcode is provided and is exactly 4 digits, filter based on zipcode-title mapping
            if (filters.Zipcode && filters.Zipcode.length === 4) {
              const matchingTitles = zipcodeTitleMap[filters.Zipcode] || [];
              
              // Include national titles as well
              const isNationalOrMatchingTitle = 
                (item.Provincie === 'Nationaal' || item.Provincie === 'Nederland') ||
                matchingTitles.includes(item.Titel);
              
              return isNationalOrMatchingTitle &&
                    (!filters.Titel || item.Titel === filters.Titel);
            }
            
            // For region search with provincie, include items that match the selected province
            // OR items that are 'Nationaal' or 'Nederland' (available everywhere)
            if (filters.Provincie && item.Provincie) {
              return (item.Provincie === filters.Provincie || 
                    item.Provincie === 'Nationaal' || 
                    item.Provincie === 'Nederland') &&
                    (!filters.Titel || item.Titel === filters.Titel);
            }
            
            return false;
          });
        } else {
          // For filter and step-by-step modes, use only the relevant filters
          filtered = data.filter(item => {
            // Check Mediumtype, Titel, Aandeel, Formaat, and Positie
            // Explicitly ignore Provincie and Zipcode in non-region modes
            const filtersToCheck = {
              Mediumtype: filters.Mediumtype,
              Titel: filters.Titel,
              Aandeel: filters.Aandeel,
              Formaat: filters.Formaat,
              Positie: filters.Positie
            };
            
            // Apply content type filter for Print media
            const _mt = (item.Mediumtype || '').trim();
            const isPrint =
              _mt.startsWith('Dagblad') ||
              ['Magazine', 'Nieuwsblad', 'Weekblad', 'Special', 'Weekkrant', 'Nieuws- en weekbladen', 'Print (totaal)', 'Mediahuis Connect'].includes(_mt);
            
            if (isPrint) {
              const formatStartsWith = contentType === 'advertising' ? 'D' : 'C';
              if (!item.Formaat?.startsWith(formatStartsWith)) {
                return false;
              }
            }
            


            
            return Object.entries(filtersToCheck).every(([key, value]) => {
              // Skip empty filter values
              if (!value) return true;
              
              // Special handling for Mediumtype when 'Nieuws- en weekbladen' is selected
              if (key === 'Mediumtype' && value === 'Nieuws- en weekbladen') {
                return item[key] === 'Nieuwsblad' || item[key] === 'Weekblad';
              }
              
              // Special handling for Mediumtype when 'Mediahuis Connect' is selected
              if (key === 'Mediumtype' && value === 'Mediahuis Connect') {
                const t = (item.Titel || '').trim();
                return item.Mediumtype?.startsWith('Dagblad') && t.includes('Mediahuis');
              }
              
              // Special handling for Mediumtype when 'Dagblad' is selected - exclude Mediahuis titles
              if (key === 'Mediumtype' && value?.startsWith('Dagblad')) {
                const t = (item.Titel || '').trim();
                return item.Mediumtype?.startsWith('Dagblad') && (!t || !t.includes('Mediahuis'));
              }
              
        // Standard equality check for other filters
        if (key === 'Titel') {
          return (item.Titel || '').trim() === (value || '').trim();
        }
        if (key === 'Aandeel') {
          return formatAandeelDisplay(item.Aandeel) === value;
        }
        return item[key] === value;
            });
          });
        }
        
        setFilteredData(filtered);
        setIsFiltering(false);
      } else {
        setFilteredData([]);
      }
    };

    // Only apply filters if we have a complete zipcode or a province selected
    if (selectionMode === 'region' && filters.Zipcode && filters.Zipcode.length < 4) {
      setFilteredData([]);
      return;
    }

    applyFilters();
  }, [data, filters, selectionMode, currentStep, regionStep, zipcodeTitleMap, contentType]);

  const getUniqueValues = (column) => {
    let values;
    
    // Early return if data is not loaded yet
    if (!data || data.length === 0) {
      if (column === 'Provincie') {
        // For provinces, we want to show the 12 provinces only
        const allProvinces = [
          'Drenthe',
          'Flevoland',
          'Friesland',
          'Gelderland',
          'Groningen',
          'Limburg',
          'Noord-Brabant',
          'Noord-Holland',
          'Overijssel',
          'Utrecht',
          'Zeeland',
          'Zuid-Holland'
        ];
        return allProvinces;
      }
      return [];
    }
    
    if (column === 'Provincie') {
      // For provinces, we want to show the 12 provinces only
      const allProvinces = [
        'Drenthe',
        'Flevoland',
        'Friesland',
        'Gelderland',
        'Groningen',
        'Limburg',
        'Noord-Brabant',
        'Noord-Holland',
        'Overijssel',
        'Utrecht',
        'Zeeland',
        'Zuid-Holland'
      ];
      
      // Return all provinces regardless of what's in the data
      return allProvinces;
    } else if (column === 'Mediumtype') {
      // Define the medium groups and their sub-types
      const mediumGroups = {
        'Online': ['Display', 'Video', 'Nieuwsbrief', 'Online', 'Social'],
        'Print': [
          'Dagblad',
          'Magazine',
          'Nieuws- en weekbladen',
          'Nieuwsblad',
          'Print (totaal)',
          'Special',
          'Weekkrant',
          'Mediahuis Connect',
        ],
        'Audio': [
          'Radio',
          'Podcast'
        ]
      };
      
      // Get all unique values from data with normalization:
      // - Combine 'Nieuwsblad' and 'Weekblad' into 'Nieuws- en weekbladen'
      // - Normalize any 'Dagblad, ...' into 'Dagblad'
      const uniqueValues = [...new Set(data.map(item => {
        const mt = item[column];
        if (mt === 'Nieuwsblad' || mt === 'Weekblad') {
          return 'Nieuws- en weekbladen';
        }
        if (typeof mt === 'string' && mt.startsWith('Dagblad')) {
          return 'Dagblad';
        }
        return mt;
      }))];
      
      // Create array with headers and their values
      values = [];
      Object.entries(mediumGroups).forEach(([header, items]) => {
        // Add the header
        values.push({ value: header, isHeader: true });
        // Add the items that exist in our data and have non-empty title configurations
        items.forEach(item => {
          if (uniqueValues.includes(item)) {
            // Check if this medium type has a title configuration
            const hasTitleConfig = titleFilterConfig[item] !== undefined;
            // Check if the configuration is not empty (has titles or wildcard)
            const hasTitles = hasTitleConfig && (
              titleFilterConfig[item].includes('*') || 
              titleFilterConfig[item].length > 0
            );
            
            // Only add medium types that have title configurations with available titles
            if (hasTitles) {
              values.push({ value: item, isHeader: false });
            }
          }
        });
        // Always add 'Mediahuis Connect' under Print if there are any such products and it has titles configured
        if (header === 'Print') {
          const hasMediahuisConfig = titleFilterConfig['Mediahuis Connect'] !== undefined;
          const hasMediahuisTitles = hasMediahuisConfig && (
            titleFilterConfig['Mediahuis Connect'].includes('*') || 
            titleFilterConfig['Mediahuis Connect'].length > 0
          );
          
          if (hasMediahuisTitles) {
            values.push({ value: 'Mediahuis Connect', isHeader: false });
          }
        }
      });
      
      return values;
    } else if (column === 'Titel' && filters.Mediumtype) {
      // Filter Titel options based on selected Mediumtype
      // Special handling for 'Nieuws- en weekbladen'
      if (filters.Mediumtype === 'Nieuws- en weekbladen') {
        values = [...new Set(
          data
            .filter(item => 
              (item.Mediumtype === 'Nieuwsblad' || item.Mediumtype === 'Weekblad') &&
              shouldShowTitle('Nieuws- en weekbladen', item.Titel)
            )
            .map(item => item[column])
        )];
      } else if (filters.Mediumtype === 'Mediahuis Connect') {
        // For Mediahuis Connect, show all titles containing 'Mediahuis' from Dagblad
        values = [...new Set(
          data
            .filter(item => 
              item.Mediumtype?.startsWith('Dagblad') &&
              (item.Titel || '').trim().includes('Mediahuis') &&
              shouldShowTitle('Mediahuis Connect', (item.Titel || '').trim())
            )
            .map(item => (item[column] || '').trim())
        )];
      } else if (filters.Mediumtype?.startsWith('Dagblad')) {
        // For Dagblad, exclude titles containing 'Mediahuis' (they go to Mediahuis Connect)
        let filteredData = data.filter(item => 
          item.Mediumtype?.startsWith('Dagblad') &&
          (!item.Titel || !item.Titel.includes('Mediahuis')) &&
          shouldShowTitle('Dagblad', item.Titel)
        );
        values = [...new Set(filteredData.map(item => item[column]))];
      } else if (filters.Mediumtype === 'Print (totaal)') {
        values = [...new Set(
          data
            .filter(item => 
              item.Mediumtype === filters.Mediumtype &&
              shouldShowTitle('Print (totaal)', item.Titel)
            )
            .map(item => item[column])
        )];
      } else if (filters.Mediumtype === 'Magazine' || filters.Mediumtype === 'Nieuwsblad' || filters.Mediumtype === 'Weekblad' || 
                 filters.Mediumtype === 'Special' || filters.Mediumtype === 'Weekkrant') {
        // For other Print media types, show all titles
        values = [...new Set(
          data
            .filter(item => 
              item.Mediumtype === filters.Mediumtype &&
              shouldShowTitle(filters.Mediumtype, item.Titel)
            )
            .map(item => item[column])
        )];
      } else {
        // For non-Print media, show all titles
        values = [...new Set(
          data
            .filter(item => item.Mediumtype === filters.Mediumtype)
            .map(item => item[column])
        )];
      }
      
      // Sort titles to ensure 'De Telegraaf' always appears first
      if (column === 'Titel' && values && values.length > 0) {
        values.sort((a, b) => {
          // If 'De Telegraaf' is in the title, prioritize it
          const aIsTelegraaf = a && a.includes('De Telegraaf');
          const bIsTelegraaf = b && b.includes('De Telegraaf');
          
          if (aIsTelegraaf && !bIsTelegraaf) return -1;
          if (!aIsTelegraaf && bIsTelegraaf) return 1;
          
          // If both or neither contain 'De Telegraaf', sort alphabetically
          return a.localeCompare(b);
        });
      }
      
      return values;
    } else if ((column === 'Aandeel' || column === 'Formaat') && filters.Mediumtype && filters.Titel) {
      // Determine which field to use based on medium type
      const _mtSelected = (filters.Mediumtype || '').trim();
      const useAandeel = isPrintMediumType(filters.Mediumtype);
      
      // Use Aandeel for Print media, Formaat for others (e.g. Nieuwsbrief, Display, Video)
      const fieldToUse = useAandeel ? 'Aandeel' : 'Formaat';
      
      // Only proceed if we're looking for the correct field
      if (column === fieldToUse) {
        let filteredData;
        
        if (filters.Mediumtype === 'Nieuws- en weekbladen') {
          filteredData = data.filter(item => 
            (item.Mediumtype === 'Nieuwsblad' || item.Mediumtype === 'Weekblad') && 
            item.Titel === filters.Titel &&
            shouldShowTitle('Nieuws- en weekbladen', item.Titel)
          );
        } else if (filters.Mediumtype === 'Mediahuis Connect') {
          // For Mediahuis Connect, filter by titles containing 'Mediahuis' from Dagblad
          filteredData = data.filter(item => 
          item.Mediumtype?.startsWith('Dagblad') && 
          (item.Titel || '').trim() === (filters.Titel || '').trim() &&
          (item.Titel || '').trim().includes('Mediahuis') &&
            shouldShowTitle('Mediahuis Connect', item.Titel)
          );
        } else if (filters.Mediumtype?.startsWith('Dagblad')) {
          // For Dagblad, exclude titles containing 'Mediahuis'
          filteredData = data.filter(item => 
            item.Mediumtype?.startsWith('Dagblad') && 
            item.Titel === filters.Titel &&
            (!item.Titel || !item.Titel.includes('Mediahuis')) &&
            shouldShowTitle('Dagblad', item.Titel)
          );
        } else if (filters.Mediumtype === 'Print (totaal)') {
          filteredData = data.filter(item => 
            item.Mediumtype === filters.Mediumtype && 
            item.Titel === filters.Titel &&
            shouldShowTitle('Print (totaal)', item.Titel)
          );
        } else {
          const isPrintMedium = ['Magazine', 'Nieuwsblad', 'Weekblad', 
            'Special', 'Weekkrant'].includes(filters.Mediumtype);
          
          if (isPrintMedium) {
            filteredData = data.filter(item => 
              item.Mediumtype === filters.Mediumtype && 
              item.Titel === filters.Titel &&
              shouldShowTitle(filters.Mediumtype, item.Titel)
            );
          } else {
            filteredData = data.filter(item => 
              item.Mediumtype === filters.Mediumtype && 
              item.Titel === filters.Titel
            );
          }
        }
        
        // Filter by Position if it's selected (to show only compatible Format/Aandeel options)
        if (filters.Positie) {
          filteredData = filteredData.filter(item => item.Positie === filters.Positie);
        }
        
        values = column === 'Aandeel'
          ? [...new Set(filteredData.map(item => formatAandeelDisplay(item[fieldToUse])).filter(Boolean))]
          : [...new Set(filteredData.map(item => item[fieldToUse]))];
        
        // Custom sorting for Aandeel values
        if (column === 'Aandeel') {
          values.sort((a, b) => {
            // Convert fractions to decimal for comparison
            const getDecimalValue = (value) => {
              if (value.includes('/')) {
                const [num, denom] = value.split('/');
                return parseFloat(num) / parseFloat(denom);
              }
              return parseFloat(value);
            };
            
            const aValue = getDecimalValue(a);
            const bValue = getDecimalValue(b);
            
            // Sort in descending order (largest first)
            return bValue - aValue;
          });
        } else {
          // Default sort for other fields
          values.sort();
        }
      } else {
        values = [];
      }
    } else if (column === 'Positie' && filters.Mediumtype && filters.Titel) {
      let filteredData;
      
      if (filters.Mediumtype === 'Nieuws- en weekbladen') {
        filteredData = data.filter(item => 
          (item.Mediumtype === 'Nieuwsblad' || item.Mediumtype === 'Weekblad') && 
          item.Titel === filters.Titel
        );
      } else if (filters.Mediumtype === 'Mediahuis Connect') {
        // For Mediahuis Connect, filter by titles containing 'Mediahuis' from Dagblad
        filteredData = data.filter(item => 
          item.Mediumtype?.startsWith('Dagblad') && 
          item.Titel === filters.Titel &&
          item.Titel && item.Titel.includes('Mediahuis')
        );
      } else if (filters.Mediumtype === 'Dagblad') {
        // For Dagblad, exclude titles starting with 'Mediahuis'
        filteredData = data.filter(item => 
          item.Mediumtype?.startsWith('Dagblad') && 
          item.Titel === filters.Titel &&
          (!item.Titel || !item.Titel.startsWith('Mediahuis'))
        );
      } else if (filters.Mediumtype === 'Print (totaal)') {
        filteredData = data.filter(item => 
          item.Mediumtype === filters.Mediumtype && 
          item.Titel === filters.Titel
        );
      } else {
        const isPrintMedium = ['Magazine', 'Nieuwsblad', 'Weekblad', 
          'Special', 'Weekkrant'].includes(filters.Mediumtype);
        
        if (isPrintMedium) {
          filteredData = data.filter(item => 
            item.Mediumtype === filters.Mediumtype && 
            item.Titel === filters.Titel
          );
        } else {
          filteredData = data.filter(item => 
            item.Mediumtype === filters.Mediumtype && 
            item.Titel === filters.Titel
          );
        }
      }
      
      // Further filter by Aandeel or Formaat based on medium type (if selected)
      const isPrint = isPrintMediumType(filters.Mediumtype);
      
      if (isPrint && filters.Aandeel) {
        filteredData = filteredData.filter(item => formatAandeelDisplay(item.Aandeel) === filters.Aandeel);
      } else if (!isPrint && filters.Formaat) {
        filteredData = filteredData.filter(item => item.Formaat === filters.Formaat);
      }
      
      // Position options are now available even without Format/Aandeel selected
      // They will be filtered by Format/Aandeel if those are selected
      
      values = [...new Set(filteredData.map(item => item[column]))];
    } else if (column === 'Provincie') {
      // For Provincie, only show provinces that have print media products
      values = [...new Set(
        data
          .filter(item => {
            const mt = (item.Mediumtype || '').trim();
            return mt.startsWith('Dagblad') || ['Magazine','Nieuwsblad','Weekblad','Print (totaal)','Special','Weekkrant'].includes(mt);
          })
          .map(item => item[column])
      )];
    } else if (column === 'Titel') {
      // REGION MODE: If searching by zipcode, show local titles + national (available everywhere) in two blocks
      if (selectionMode === 'region' && filters.Zipcode && filters.Zipcode.length === 4) {
        const isPrintMedia = (item) => {
          const mt = (item.Mediumtype || '').trim();
          return mt.startsWith('Dagblad') || ['Magazine','Nieuwsblad','Weekblad','Print (totaal)','Special','Weekkrant'].includes(mt);
        };
        // Local block: titles for this zipcode that exist in data (print only)
        const localTitles = (zipcodeTitleMap[filters.Zipcode] || [])
          .filter(title => data.some(item => isPrintMedia(item) && item.Titel === title))
          .filter(Boolean);
        // National block: unique titles with Provincie Nationaal/Nederland (print only), exclude those already in local
        const nationalTitles = [...new Set(
          data
            .filter(item => isPrintMedia(item) && (item.Provincie === 'Nationaal' || item.Provincie === 'Nederland'))
            .map(item => item.Titel)
            .filter(Boolean)
        )].filter(title => !localTitles.includes(title));

        const result = [];
        // Local block with header
        result.push({ value: t('localPrintMedia', 'Print media in your region'), isHeader: true, category: 'local' });
        localTitles.forEach(title => result.push({ value: title, category: 'local' }));
        // National block with header (available everywhere)
        result.push({ value: t('availableEverywhere', 'Available everywhere'), isHeader: true, category: 'national' });
        nationalTitles.forEach(title => result.push({ value: title, category: 'national' }));
        return result;
      }
      // REGION MODE: If searching by province, filter titles by province
      if (selectionMode === 'region' && filters.Provincie) {
        // Filter data to only include items that match the selected province
        // OR items that are 'Nationaal' or 'Nederland' (available everywhere)
        const filteredData = data.filter(item => {
          const mt = (item.Mediumtype || '').trim();
          const isPrintMedia = mt.startsWith('Dagblad') || ['Magazine','Nieuwsblad','Weekblad','Print (totaal)','Special','Weekkrant'].includes(mt);
          if (!isPrintMedia) return false;
          
          return (item.Provincie === filters.Provincie || 
                item.Provincie === 'Nationaal' || 
                item.Provincie === 'Nederland');
        });
        
        // Get unique titles from the filtered data
        const titles = [...new Set(filteredData.map(item => item[column]))];
        return titles
          .filter(Boolean)
          .map(title => ({ value: title }));
      }
      // ...existing logic for other modes...
    } else {
      values = [...new Set(data.map(item => item[column]))];
    }
    
    return (values || []).filter(Boolean).sort();
  };

  const handleFilterChange = (column, value) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [column]: value
      };
      
      // Reset dependent fields when parent fields change
      if (column === 'Mediumtype') {
        newFilters.Titel = '';
        newFilters.Aandeel = '';
        newFilters.Formaat = '';
        newFilters.Positie = '';
        // Reset selected row when Mediumtype changes
        setSelectedRow(null);
      } else if (column === 'Titel') {
        newFilters.Aandeel = '';
        newFilters.Formaat = '';
        newFilters.Positie = '';
        // Reset selected row when Titel changes
        setSelectedRow(null);
      } else if (column === 'Aandeel' || column === 'Formaat') {
        // Don't reset Positie - they filter each other
      } else if (column === 'Positie') {
        // Don't reset Aandeel or Formaat - they filter each other
      }
      
      return newFilters;
    });
  };

  const isFilterDisabled = (filterKey) => {
    if (filterKey === 'Titel') {
      return !filters.Mediumtype;
    }
    
    const mtForDisable = (filters.Mediumtype || '').trim();
    const isPrintMedium = mtForDisable.startsWith('Dagblad') ||
      ['Magazine', 'Nieuwsblad', 'Weekblad', 'Special', 'Weekkrant', 'Nieuws- en weekbladen', 'Print (totaal)', 'Mediahuis Connect'].includes(mtForDisable);
    
    if (filterKey === 'Aandeel') {
      return !filters.Mediumtype || !filters.Titel || !isPrintMedium;
    }
    if (filterKey === 'Formaat') {
      return !filters.Mediumtype || !filters.Titel || isPrintMedium;
    }
    if (filterKey === 'Positie') {
      // Position is now selectable once Mediumtype and Titel are selected
      return !filters.Mediumtype || !filters.Titel;
    }
    return false;
  };

  const getPlaceholderText = (filterKey) => {
    // If a value is already selected for this filter, return "Reset"
    if (filters[filterKey]) {
      return t('resetFilter', "Reset");
    }
    
    if (filterKey === 'Titel' && !filters.Mediumtype) {
      return t('selectMediumtypeFirst', 'First select Mediumtype');
    }
    
    const isPrint = isPrintMediumType(filters.Mediumtype);
    
    if (filterKey === 'Aandeel' && (!filters.Mediumtype || !filters.Titel || !isPrint)) {
      return t('selectMediumtypeAndTitelFirst', 'First select Mediumtype and Titel');
    }
    if (filterKey === 'Formaat' && (!filters.Mediumtype || !filters.Titel || isPrint)) {
      return t('selectMediumtypeAndTitelFirst', 'First select Mediumtype and Titel');
    }
    if (filterKey === 'Positie') {
      if (!filters.Mediumtype || !filters.Titel) {
        return t('selectMediumtypeAndTitelFirst', 'First select Mediumtype and Titel');
      }
    }
    return t('selectGeneric', 'Select {field}', { field: filterKey });
  };

  const showResults = (selectionMode === 'filter' && filters.Mediumtype && filters.Titel) || 
                      (selectionMode === 'step-by-step' && currentStep > 2 && filters.Mediumtype && filters.Titel) ||
                      (selectionMode === 'region' && ((regionStep === 1 && filters.Provincie && filteredData.length > 0) || 
                                                     (regionStep === 1 && filters.Zipcode && filters.Zipcode.length === 4 && filteredData.length > 0) ||
                                                     (regionStep === 2 && (filters.Provincie || (filters.Zipcode && filters.Zipcode.length === 4)) && filters.Titel)));

  const getDisplayColumns = (mediumtype) => {
    const baseColumns = ['Titel', 'Positie', 'Basisprijs'];
    
    // Add Aandeel for Print media, Formaat for others (e.g. Nieuwsbrief, Display, Video)
    if (mediumtype === 'Print' || isPrintMediumType(mediumtype)) {
      baseColumns.splice(1, 0, 'Aandeel');
    } else {
      baseColumns.splice(1, 0, 'Formaat');
    }
    
    switch (mediumtype) {
      case 'Print':
        return [...baseColumns, 'Plaatsingsdag'];
      case 'Online':
      case 'Audio':
      case 'Radio':
        return baseColumns;
      default:
        return baseColumns;
    }
  };

  // Convert options for CustomSelect component
  const getSelectOptions = (filterKey) => {
    const options = [];
    
    // Add placeholder option
    options.push({
      value: '',
      label: getPlaceholderText(filterKey),
      disabled: false
    });
    
    if (filterKey === 'Mediumtype') {
      getUniqueValues(filterKey).forEach(item => {
        if (item.isHeader) {
          options.push({
            value: item.value,
            label: item.value,
            isHeader: true,
            disabled: true
          });
        } else {
          options.push({
            value: item.value,
            label: item.value,
            className: 'filter-option-item',
            disabled: false
          });
        }
      });
    } else if (filterKey === 'Aandeel') {
      // Get and sort Aandeel values
      const values = getUniqueValues(filterKey);
      
      // Custom sorting for Aandeel values
      values.sort((a, b) => {
        // Convert fractions to decimal for comparison
        const getDecimalValue = (value) => {
          if (value.includes('/')) {
            const [num, denom] = value.split('/');
            return parseFloat(num) / parseFloat(denom);
          }
          return parseFloat(value);
        };
        
        const aValue = getDecimalValue(a);
        const bValue = getDecimalValue(b);
        
        // Sort in descending order (largest first)
        return bValue - aValue;
      });
      
      // Add sorted values to options
      values.forEach(value => {
        options.push({
          value: value,
          label: value,
          disabled: false
        });
      });
    } else {
      getUniqueValues(filterKey).forEach(value => {
        options.push({
          value: value,
          label: value,
          disabled: false
        });
      });
    }
    
    return options;
  };

  // Handle selection mode change
  const handleSelectionModeChange = (mode) => {
    setSelectionMode(mode);
    
    // Clear selected row when changing modes
    setSelectedRow(null);
    
    // Cache the current values we want to preserve
    const currentTitel = filters.Titel;
    const currentProvincie = filters.Provincie;
    const currentZipcode = filters.Zipcode;
    const currentMediumtype = filters.Mediumtype;
    
    // When switching between region and other modes
    if (mode === 'region' && selectionMode !== 'region') {
      // Switching TO region mode FROM another mode
      // Preserve values that were previously set in region mode
      setFilters(prev => ({
        Mediumtype: '',
        Titel: currentTitel || '', // Preserve Titel if it exists
        Aandeel: '',
        Positie: '',
        Provincie: currentProvincie || '', // Preserve Provincie if it exists
        Zipcode: currentZipcode || '' // Preserve Zipcode if it exists
      }));
      
      // If we have province or zipcode data, move to step 2
      if (currentProvincie || (currentZipcode && currentZipcode.length === 4)) {
        setRegionStep(2);
      } else {
        // Otherwise, start from step 1
        setRegionStep(1);
      }
      
      return;
    } else if (selectionMode === 'region' && mode !== 'region') {
      // Switching FROM region mode TO another mode
      // Keep Provincie and Zipcode in memory but don't use them in other modes
      // This way they're still available when going back to region mode
      setFilters(prev => ({
        ...prev,
        Mediumtype: mode === 'step-by-step' && currentMediumtype ? currentMediumtype : '',
        Titel: currentTitel || '',
        Aandeel: '',
        Positie: ''
        // Keep Provincie and Zipcode as they are
      }));
      
      // Set appropriate step for step-by-step mode
      if (mode === 'step-by-step') {
        if (filters.Aandeel && filters.Positie) {
          setCurrentStep(4);
        } else if (filters.Aandeel) {
          setCurrentStep(3);
        } else if (filters.Titel && filters.Mediumtype) {
          setCurrentStep(3); // Go to Aandeel selection if both Titel and Mediumtype exist
        } else if (filters.Mediumtype) {
          setCurrentStep(2);
        } else {
          setCurrentStep(1);
        }
      }
      
      return;
    }
    
    // For switching between filter and step-by-step modes, preserve filters
    if (mode === 'step-by-step') {
      // When switching to step-by-step mode, set the appropriate step based on filters
      if (filters.Aandeel && filters.Positie) {
        setCurrentStep(4); // Aandeel step (final step)
      } else if (filters.Positie) {
        setCurrentStep(3); // Position step
      } else if (filters.Mediumtype) {
        setCurrentStep(2); // Titel step
      } else {
        setCurrentStep(1); // Start from beginning
      }
      
      // Show notification if there are active filters
      if (filters.Mediumtype || filters.Titel || filters.Aandeel || filters.Positie) {
        setShowFilterNotification(true);
        // Hide notification after 5 seconds
        setTimeout(() => {
          setShowFilterNotification(false);
        }, 5000);
      }
    } else {
      // When switching to filter mode, keep the existing filters
      // Show notification if there are active filters
      if (filters.Mediumtype || filters.Titel || filters.Aandeel || filters.Positie) {
        setShowFilterNotification(true);
        // Hide notification after 5 seconds
        setTimeout(() => {
          setShowFilterNotification(false);
        }, 5000);
      }
    }
  };

  // Handle step-by-step selection
  const handleStepSelection = (value) => {
    if (currentStep === 1) {
      // Selected Mediumtype
      setFilters(prev => ({
        ...prev,
        Mediumtype: value,
        Titel: '',
        Aandeel: '',
        Positie: ''
      }));
      setCurrentStep(2);
      // Reset selected row when changing medium type
      setSelectedRow(null);
    } else if (currentStep === 2) {
      // Selected Titel
      setFilters(prev => ({
        ...prev,
        Titel: value,
        Aandeel: '',
        Positie: ''
      }));
      setCurrentStep(3);
      // Reset selected row when changing title
      setSelectedRow(null);
    } else if (currentStep === 3) {
      // Selected Aandeel or Formaat based on medium type (Nieuwsbrief is Digital → Formaat)
      const isPrint = isPrintMediumType(filters.Mediumtype);
      
      if (isPrint) {
        // For print media, set Aandeel
        setFilters(prev => ({
          ...prev,
          Aandeel: value,
          Formaat: '', // Clear Formaat when setting Aandeel
          Positie: ''
        }));
      } else {
        // For digital media, set Formaat
        setFilters(prev => ({
          ...prev,
          Formaat: value,
          Aandeel: '', // Clear Aandeel when setting Formaat
          Positie: ''
        }));
      }
      setCurrentStep(4);
      // Reset selected row when changing Aandeel/Formaat
      setSelectedRow(null);
    } else if (currentStep === 4) {
      // Selected Position
      setFilters(prev => ({
        ...prev,
        Positie: value
      }));
      // Reset selected row when changing position
      setSelectedRow(null);
      
      // Scroll to results section after a short delay to allow for state updates
      setTimeout(() => {
        const resultsSection = document.querySelector('.step-results-container');
        console.log('try to scroll to results section for step 4', resultsSection);
        
        // Function to scroll with additional offset from the top
        const scrollWithOffset = (element, offset = 100) => {
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        };
        
        if (resultsSection) {
          scrollWithOffset(resultsSection, 100);
        } else {
          // If element not found, use MutationObserver to watch for it
          console.log('Results section not found, setting up observer');
          const observer = new MutationObserver((mutations, obs) => {
            const resultsSection = document.querySelector('.step-results-container');
            if (resultsSection) {
              console.log('Results section found by observer');
              scrollWithOffset(resultsSection, 100);
              obs.disconnect(); // Stop observing once found
            }
          });
          
          // Start observing the document with the configured parameters
          observer.observe(document.body, { childList: true, subtree: true });
          
          // Disconnect after 5 seconds to prevent memory leaks
          setTimeout(() => observer.disconnect(), 5000);
        }
      }, 300); // Increased delay to give more time for rendering
    }
  };

  // Handle region step selection
  const handleRegionStepSelection = (value) => {
    // Skip if it's a header
    if (typeof value === 'object' && value.isHeader) {
      return;
    }
    
    // Extract the actual value if it's an object
    const titleValue = typeof value === 'object' ? value.value : value;
    
    if (regionStep === 1) {
      // Selected Province
      setFilters(prev => ({
        ...prev,
        Provincie: titleValue,
        Zipcode: '', // Clear zipcode when province is selected
        Titel: '',
        Aandeel: ''
      }));
      setRegionStep(2);
      // Reset selected row when changing regions
      setSelectedRow(null);
    } else if (regionStep === 2) {
      // Selected Title - go directly to results
      setFilters(prev => ({
        ...prev,
        Titel: titleValue,
        Aandeel: ''
      }));
      // Reset selected row when changing titles
      setSelectedRow(null);
      
      // Scroll to results section after a short delay to allow for state updates
      setTimeout(() => {
        const resultsSection = document.querySelector('.step-results-container');
        console.log('try to scroll to results section', resultsSection);
        
        // Function to scroll with additional offset from the top
        const scrollWithOffset = (element, offset = 100) => {
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        };
        
        if (resultsSection) {
          scrollWithOffset(resultsSection, 100);
        } else {
          // If element not found, use MutationObserver to watch for it
          console.log('Results section not found, setting up observer');
          const observer = new MutationObserver((mutations, obs) => {
            const resultsSection = document.querySelector('.step-results-container');
            if (resultsSection) {
              console.log('Results section found by observer');
              scrollWithOffset(resultsSection, 100);
              obs.disconnect(); // Stop observing once found
            }
          });
          
          // Start observing the document with the configured parameters
          observer.observe(document.body, { childList: true, subtree: true });
          
          // Disconnect after 5 seconds to prevent memory leaks
          setTimeout(() => observer.disconnect(), 5000);
        }
      }, 300); // Increased delay to give more time for rendering
    }
  };

  // Handle zipcode input change
  const handleZipcodeChange = (event) => {
    const value = event.target.value.trim();
    
    // Only allow 4 digits
    if (value === '' || (/^\d{0,4}$/.test(value))) {
      setFilters(prev => ({
        ...prev,
        Zipcode: value,
        Provincie: value ? '' : prev.Provincie, // Clear province when zipcode is entered
        Titel: '' // Clear title when zipcode changes
      }));
      
      // Clear location data if zipcode is cleared or incomplete
      if (value.length < 4) {
        setZipcodeLocation(null);
      }
      
      if (value.length === 4) {
        // Look up location data when a valid zipcode is entered
        
        // Special handling for testing zipcode 1211 (since it's a specific example)
        if (value === '1211') {
          // Manually create the Hilversum entry as a fallback
          console.log('Special case for 1211 - Setting Hilversum data');
          const hilversumData = {
            city: 'Hilversum',
            municipality: 'Hilversum',
            province: 'Noord-Holland'
          };
          setZipcodeLocation(hilversumData);
          setRegionStep(2);
          return;
        }
        
        const locationData = zipcodeLocationData[value];
        console.log('Looking up zipcode:', value, 'Found:', locationData);
        
        setZipcodeLocation(locationData || null);
        setRegionStep(2); // Move to title selection when a valid zipcode is entered
      } else {
        setRegionStep(1); // Stay at first step if zipcode is incomplete
        // Clear selected row when changing zipcode
        setSelectedRow(null);
      }
    }
  };

  // Map abbreviated region names to their full names
  const getFullRegionName = (abbreviatedName) => {
    // Handle special cases with multiple regions
    if (abbreviatedName === 'Gr, Fr, Dr, Lim') {
      return 'Groningen, Friesland, Drenthe, Limburg';
    } else if (abbreviatedName === 'Gr, Fr, Dr, NH, ZH') {
      return 'Groningen, Friesland, Drenthe, Noord-Holland, Zuid-Holland';
    } else if (abbreviatedName === 'Gr, Fr, Dr, NH, ZH, Lim') {
      return 'Groningen, Friesland, Drenthe, Noord-Holland, Zuid-Holland, Limburg';
    } else if (abbreviatedName === 'Gr, Fr, Dr, NH, ZH, Lim, Fl, Ov') {
      return 'Groningen, Friesland, Drenthe, Noord-Holland, Zuid-Holland, Limburg, Flevoland, Overijssel';
    } else if (abbreviatedName === 'NH, ZH, Lim') {
      return 'Noord-Holland, Zuid-Holland, Limburg';
    } else if (abbreviatedName === 'Groningen/Drenthe') {
      return 'Groningen, Drenthe';
    } else if (abbreviatedName === 'Groningen/Friesland/Drenthe') {
      return 'Groningen, Friesland, Drenthe';
    } else if (abbreviatedName === 'Noord- en Zuid-Holland') {
      return 'Noord-Holland, Zuid-Holland';
    } else if (abbreviatedName === 'Nationaal' || abbreviatedName === 'Nederland') {
      return 'Available in all regions';
    }

    // For single regions or unmatched patterns, return as is
    return abbreviatedName;
  };

  // Go back to previous step
  const handleStepBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      
      // Clear the value for the current step
      if (currentStep === 2) {
        setFilters(prev => ({
          ...prev,
          Mediumtype: ''
        }));
      } else if (currentStep === 3) {
        setFilters(prev => ({
          ...prev,
          Titel: ''
        }));
      } else if (currentStep === 4) {
        setFilters(prev => ({
          ...prev,
          Aandeel: ''
        }));
      }
      
      // Clear selected row when going back
      setSelectedRow(null);
    }
  };

  // Go back to previous region step
  const handleRegionStepBack = () => {
    if (regionStep > 1) {
      setRegionStep(regionStep - 1);
      
      // Clear the values for the current step and zipcode
      if (regionStep === 2) {
        setFilters(prev => ({
          ...prev,
          Titel: '',
          Aandeel: '',
          Zipcode: '' // Clear zipcode when going back
        }));
      } else if (regionStep === 3) {
        setFilters(prev => ({
          ...prev,
          Aandeel: ''
        }));
      }
      
      // Clear selected row when going back
      setSelectedRow(null);
      
      // Clear filtered data when going back to step 1
      if (regionStep === 2) {
        setFilteredData([]);
      }
    }
  };

  // Get icon for Mediumtype
  const getMediumtypeIcon = (mediumtype) => {
    // Print mediumtypes
    const printTypes = [
      'Dagblad',
      'Magazine',
      'Nieuws- en weekbladen',
      'Nieuwsblad',
      'Print (totaal)',
      'Special',
      'Weekblad',
      'Weekkrant',
      'Mediahuis Connect'
    ];
    
    if (printTypes.includes(mediumtype)) {
      return `${process.env.PUBLIC_URL}/assets/mediahuis/MH-Print.png`;
    } else if (mediumtype === 'Online') {
      return `${process.env.PUBLIC_URL}/assets/mediahuis/MH-Display-advertising-.png`;
    } else if (mediumtype === 'Nieuwsbrief') {
      return `${process.env.PUBLIC_URL}/assets/mediahuis/MH-Display-advertising-.png`;
    } else if (mediumtype === 'Podcast') {
      return `${process.env.PUBLIC_URL}/assets/mediahuis/MH-Podcast.png`;
    } else if (mediumtype === 'Radio' || mediumtype === 'Audio') {
      return `${process.env.PUBLIC_URL}/assets/mediahuis/MH-Radio.png`;
    } else if (mediumtype === 'Social') {
      return `${process.env.PUBLIC_URL}/assets/mediahuis/MH-Social-Media.png`;
    }
    
    // Default icon if no match
    return `${process.env.PUBLIC_URL}/assets/mediahuis/MH-Print.png`;
  };

  // Function to get brand logo filename based on title (same as in CardsList)
  const getBrandLogo = (title) => {
    if (!title) return 'brand-placeholder.svg';
    
    // First check if we have a mapping for this brand
    const mappedLogo = getBrandLogoFilename(title);
    if (mappedLogo) {
      return mappedLogo;
    }
    
    // If no mapping found, fall back to the original logic
    // Remove .nl from the title first
    const titleWithoutDomain = title.replace(/\.nl/gi, '');
    
    // Clean up the title to create a potential filename
    // Convert to lowercase and remove special characters
    const cleanTitle = titleWithoutDomain.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')  // Remove special characters
      .trim()
      .replace(/\s+/g, ' ');        // Normalize spaces
    
    // Try different variations of the title as potential filenames
    const potentialNames = [
      cleanTitle,                                // Full cleaned title with spaces
      cleanTitle.split(' ')[0],                  // First word
      cleanTitle.replace(/\s+/g, '')             // No spaces
    ];
    
    // Return the first potential name + .svg extension
    // The onError handler in the img tag will handle fallback if file doesn't exist
    return `${potentialNames[0]}.svg`;
  };

  // Handle switch from region mode to step-by-step mode with selected values
  const handleFilterResultsClick = () => {
    // Extract title value from region search (can be from zipcode or province search)
    const regionTitel = filters.Titel;
    const currentProvincie = filters.Provincie;
    const currentZipcode = filters.Zipcode;
    
    // Change to step-by-step selection mode
    setSelectionMode('step-by-step');
    
    // Keep the same Mediumtype (always 'Dagblad' or similar for region search) and Titel
    // We need to extract this from the selected products
    if (filteredData.length > 0) {
      const firstProduct = filteredData[0];
      
      // Set filters to preserve Mediumtype and Titel while keeping region info
      setFilters(prev => ({
        ...prev,
        Mediumtype: firstProduct.Mediumtype,
        Titel: regionTitel,
        Aandeel: '',
        Positie: '',
        // Keep Provincie and Zipcode values instead of clearing them
        Provincie: currentProvincie,
        Zipcode: currentZipcode
      }));
      
      // Set appropriate step (step 3 - Aandeel selection)
      // We skip step 2 because we already have Mediumtype and Titel
      setCurrentStep(3);
      
      // Show notification that filters have been preserved
      setShowFilterNotification(true);
      setTimeout(() => {
        setShowFilterNotification(false);
      }, 5000);
    }
  };

  // Function to get count of results for each content type
  const getContentTypeCounts = () => {
    if (!showResults || !filteredData.length || !data || data.length === 0) {
      return { advertising: 0, branded: 0 };
    }

    // Get the base filtered data without content type filtering
    const baseFilteredData = data.filter(item => {
      // Check Mediumtype, Titel, Aandeel, Formaat, and Positie
      const filtersToCheck = {
        Mediumtype: filters.Mediumtype,
        Titel: filters.Titel,
        Aandeel: filters.Aandeel,
        Formaat: filters.Formaat,
        Positie: filters.Positie
      };
      

      
      return Object.entries(filtersToCheck).every(([key, value]) => {
        // Skip empty filter values
        if (!value) return true;
        
        // Special handling for Mediumtype when 'Nieuws- en weekbladen' is selected
        if (key === 'Mediumtype' && value === 'Nieuws- en weekbladen') {
          return item[key] === 'Nieuwsblad' || item[key] === 'Weekblad';
        }
        
        // Special handling for Mediumtype when 'Mediahuis Connect' is selected
        if (key === 'Mediumtype' && value === 'Mediahuis Connect') {
      const t = (item.Titel || '').trim();
      return item.Mediumtype?.startsWith('Dagblad') && t.includes('Mediahuis');
        }
        
        // Special handling for Mediumtype when 'Dagblad' is selected - exclude Mediahuis titles
        if (key === 'Mediumtype' && value === 'Dagblad') {
      const t = (item.Titel || '').trim();
      return item.Mediumtype?.startsWith('Dagblad') && (!t || !t.includes('Mediahuis'));
        }
        
              // Standard equality check for other filters
              if (key === 'Titel') {
                return (item.Titel || '').trim() === (value || '').trim();
              }
              if (key === 'Aandeel') {
                return formatAandeelDisplay(item.Aandeel) === value;
              }
              return item[key] === value;
      });
    });

    // Count advertising (D) and branded content (C) products
    const advertisingCount = baseFilteredData.filter(item => 
      item.Formaat?.startsWith('D')
    ).length;
    
    const brandedCount = baseFilteredData.filter(item => 
      item.Formaat?.startsWith('C')
    ).length;

    return { advertising: advertisingCount, branded: brandedCount };
  };

  // Get content type counts
  const contentTypeCounts = getContentTypeCounts();
  
  // Auto-switch content type if one has 0 results
  useEffect(() => {
    if (contentTypeCounts.advertising === 0 && contentTypeCounts.branded > 0) {
      setContentType('branded');
    } else if (contentTypeCounts.branded === 0 && contentTypeCounts.advertising > 0) {
      setContentType('advertising');
    }
  }, [contentTypeCounts]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="csv-viewer">
      <HeroSlideshow />
      
      {/* Selection Mode Toggle */}
      <div className="selection-mode-toggle">
        <div className="toggle-container">
          <button 
            className={`toggle-button ${selectionMode === 'filter' ? 'active' : ''}`}
            onClick={() => handleSelectionModeChange('filter')}
          >
            {t('filterSearch', 'Filter Search')}
          </button>
          <button 
            className={`toggle-button ${selectionMode === 'step-by-step' ? 'active' : ''}`}
            onClick={() => handleSelectionModeChange('step-by-step')}
          >
            {t('stepByStepSelection', 'Step-by-Step Selection')}
          </button>
          <button 
            className={`toggle-button ${selectionMode === 'region' ? 'active' : ''}`}
            onClick={() => handleSelectionModeChange('region')}
          >
            {t('regionSearch', 'Search on region')}
          </button>
        </div>
      </div>
      
      {showFilterNotification && (
        <div className="filter-notification">
          {t('filtersPreserved', 'Your filter selections have been preserved')}
        </div>
      )}
      
      {/* Region Search Container - shown when in region mode */}
      {selectionMode === 'region' && (
        <div className="region-search-container">
          <div className="region-search-header">
            <h3>{t('searchByRegion', 'Search by Region')}</h3>
            <p>{t('selectProvincePrintOnly', 'Select a province or enter a zipcode to see print media products available in that region')}</p>
            <div className="print-media-badge">
              <img 
                src={`${process.env.PUBLIC_URL}/assets/mediahuis/MH-Print.png`} 
                alt="Print Media"
                className="print-media-icon"
              />
              <span>{t('printMediaOnly', 'Print Media Only')}</span>
            </div>
          </div>

          <div className="step-progress">
            <div className={`step-indicator ${regionStep >= 1 ? 'active' : ''}`}>
              <div className="step-number">1</div>
              <div className="step-label">{t('province', 'Province')}</div>
            </div>
            <div className="step-connector"></div>
            <div className={`step-indicator ${regionStep >= 2 ? 'active' : ''}`}>
              <div className="step-number">2</div>
              <div className="step-label">{t('stepTitel')}</div>
            </div>
          </div>

          {/* Zipcode Search Input */}
          <div className="zipcode-search">
            <h3>{t('searchByZipcode')}</h3>
            <div className="zipcode-input-container">
              <input
                type="text"
                value={filters.Zipcode}
                onChange={handleZipcodeChange}
                placeholder={t('enterZipcode')}
                maxLength="4"
                className="zipcode-input"
                disabled={isLoadingZipcodes || isLoadingLocationData}
                inputMode="numeric"
                pattern="[0-9]*"
                autocomplete="off"
              />
              {filters.Zipcode && (
                <button 
                  className="clear-zipcode" 
                  onClick={() => {
                    setFilters(prev => ({...prev, Zipcode: '', Titel: ''}));
                    setZipcodeLocation(null);
                    setRegionStep(1);
                  }}
                >
                  ✕
                </button>
              )}
            </div>
            {(isLoadingZipcodes || isLoadingLocationData) && <div className="loading-small">{t('loading')}</div>}
            {filters.Zipcode && filters.Zipcode.length < 4 && (
              <div className="zipcode-hint">{t('enterCompleteZipcode')}</div>
            )}
            
            {/* Display zipcode location information */}
            {filters.Zipcode && filters.Zipcode.length === 4 && zipcodeLocation && (
              <div className="zipcode-location-info">
                <div><strong>{zipcodeLocation.city}</strong></div>
                <div>{zipcodeLocation.municipality}, {zipcodeLocation.province}</div>
              </div>
            )}
            
            {filters.Zipcode && filters.Zipcode.length === 4 && !zipcodeLocation && !isLoadingLocationData && (
              <div className="zipcode-location-info zipcode-not-found">
                {t('zipcodeNotFound')}
              </div>
            )}
          </div>
          
          {regionStep === 1 && (
            <div className="region-separator">
              <span>{t('or')}</span>
            </div>
          )}
          
          <div className="step-content">
            {regionStep === 1 && !filters.Zipcode && (
              <div className="step-options">
                <h3>{t('selectProvince', 'Select a province')}</h3>
                <div className="options-grid">
                  {getUniqueValues('Provincie')
                    .map((value, index) => (
                      <div 
                        key={index} 
                        className="option-card"
                        onClick={() => handleRegionStepSelection(value)}
                      >
                        {value}
                      </div>
                    ))}
                </div>
                

              </div>
            )}
            
            {regionStep === 2 && (
              <div className="step-options">
                <h3>{t('selectTitel', 'Select Title')}</h3>
                <div className="selection-breadcrumb">
                  {filters.Provincie && (
                    <span className="selected-item">
                      {filters.Provincie}
                    </span>
                  )}
                  {filters.Zipcode && (
                    <span className="selected-item">
                      {t('zipcode')}: {filters.Zipcode}
                    </span>
                  )}
                </div>
                <button className="back-button" onClick={handleRegionStepBack}>
                  &larr; {t('backToProvince', 'Back to Province')}
                </button>
                <div className="options-grid">
                  {getUniqueValues('Titel').map((item, index) => {
                    if (item.isHeader) {
                      return (
                        <div key={index} className="title-category-header" data-category={item.category}>
                          <h4>{item.value}</h4>
                        </div>
                      );
                    }
                    
                    if (item.isEmpty) {
                      return (
                        <div key={index} className="title-category-empty">
                          {item.value}
                        </div>
                      );
                    }
                    
                    return (
                      <div 
                        key={index} 
                        className={`option-card  title-card ${item.category}`}
                        onClick={() => handleRegionStepSelection(item)}
                      >
                        <div className="title-logo">
                          <img 
                            src={`${process.env.PUBLIC_URL}/assets/brands/${getBrandLogo(item.value)}`} 
                            alt={item.value}
                            onError={(e) => {
                              // If this is the first error, try the first word of the title
                              if (!e.target.dataset.tried) {
                                e.target.dataset.tried = '1';
                                const titleWithoutDomain = item.value?.replace(/\.nl/gi, '');
                                const firstWord = titleWithoutDomain?.toLowerCase().split(' ')[0];
                                if (firstWord) {
                                  e.target.src = `${process.env.PUBLIC_URL}/assets/brands/${firstWord}.svg`;
                                  return;
                                }
                              }
                              // If this is the second error, try the title without spaces
                              else if (e.target.dataset.tried === '1') {
                                e.target.dataset.tried = '2';
                                const titleWithoutDomain = item.value?.replace(/\.nl/gi, '');
                                const noSpaces = titleWithoutDomain?.toLowerCase().replace(/[^a-z0-9]/g, '');
                                if (noSpaces) {
                                  e.target.src = `${process.env.PUBLIC_URL}/assets/brands/${noSpaces}.svg`;
                                  return;
                                }
                              }
                              
                              // If all attempts fail, use the placeholder
                              e.target.onerror = null;
                              e.target.src = `${process.env.PUBLIC_URL}/assets/brands/brand-placeholder.svg`;
                            }}
                          />
                        </div>
                        <div className="title-label">
                          <span>{item.value}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {!isFiltering && filteredData.length > 0 && (
                  <div className="step-results-container">
                    <h4>{t('results', 'Results')} <span className="results-count">({filteredData.length})</span></h4>
                    {selectionMode === 'region' && (
                      <button className="filter-results-button" onClick={handleFilterResultsClick}>
                        {t('filterResults', 'Filter de resultaten')}
                      </button>
                    )}
                    <div className="step-cards-list">
                      <CardsList 
                        filteredData={filteredData}
                        selectedRow={selectedRow}
                        setSelectedRow={setSelectedRow}
                        getDisplayColumns={getDisplayColumns}
                      />
                    </div>
                    
                    {selectedRow && (
                      <div className="step-details-view" id="product-details-section">
                        <h4>{t('productDetail', 'Product detail')}</h4>
                        <DetailedView 
                          selectedRow={selectedRow}
                          columns={columns}
                        />
                      </div>
                    )}
                  </div>
                )}
                
                {!isFiltering && filteredData.length === 0 && (
                  <div className="error-message">
                    {t('noResultsFound')}
                  </div>
                )}
                
                {isFiltering && (
                  <div className="loading-container">
                    <LoadingSpinner />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Filter Container - shown when in filter mode */}
      {selectionMode === 'filter' && (
        <div className="filter-container">
          <div className="filter-grid">
            {['Mediumtype', 'Titel', 
              ...(isPrintMediumType(filters.Mediumtype) ? ['Aandeel'] : ['Formaat']), 
              'Positie']
              .map(filterKey => (
              <div key={filterKey} className="filter-item" data-filter={filterKey}>
                <label className="filter-label">
                  {t(`filterLabel${filterKey}`, filterKey === 'Aandeel' ? 'Pagina aandeel' : filterKey)}
                  {(filterKey === 'Mediumtype' || filterKey === 'Titel') && 
                    <span className="required-asterisk">*</span>
                  }
                </label>
                <CustomSelect 
                  value={filters[filterKey]}
                  onChange={(e) => handleFilterChange(filterKey, e.target.value)}
                  options={getSelectOptions(filterKey)}
                  disabled={isFilterDisabled(filterKey)}
                  placeholder={getPlaceholderText(filterKey)}
                  className="filter-select"
                  t={t}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Step-by-Step Selection - shown when in step-by-step mode */}
      {selectionMode === 'step-by-step' && (
        <div className="step-selection-container">
          <div className="step-progress">
            <div className={`step-indicator ${currentStep >= 1 ? 'active' : ''}`}>
              <div className="step-number">1</div>
              <div className="step-label">{t('stepMediumtype')}</div>
            </div>
            <div className="step-connector"></div>
            <div className={`step-indicator ${currentStep >= 2 ? 'active' : ''}`}>
              <div className="step-number">2</div>
              <div className="step-label">{t('stepTitel')}</div>
            </div>
            <div className="step-connector"></div>
            <div className={`step-indicator ${currentStep >= 3 ? 'active' : ''}`}>
              <div className="step-number">3</div>
              <div className="step-label">
                {isPrintMediumType(filters.Mediumtype) ? t('stepAandeel') : t('stepFormat')}
              </div>
            </div>
            <div className="step-connector"></div>
            <div className={`step-indicator ${currentStep >= 4 ? 'active' : ''}`}>
              <div className="step-number">4</div>
              <div className="step-label">{t('stepPosition')}</div>
            </div>
          </div>
          
          <div className="step-content">
            {currentStep === 1 && (
              <div className="step-options">
                <h3>{t('selectMediumtype', 'Select Mediumtype')}</h3>
                <div className="options-grid">
                  {getUniqueValues('Mediumtype')
                    .filter(item => !item.isHeader)
                    .map((item, index) => (
                      <div 
                        key={index} 
                        className="option-card mediumtype-card"
                        onClick={() => handleStepSelection(item.value)}
                      >
                        <div className="mediumtype-icon">
                          <img 
                            src={getMediumtypeIcon(item.value)} 
                            alt={item.value}
                          />
                        </div>
                        <div className="mediumtype-label">{item.value}</div>
                      </div>
                    ))}
                </div>
              </div>
            )}
            
            {currentStep === 2 && (
              <div className="step-options">
                <h3>{t('selectTitel', 'Select Titel')}</h3>
                <div className="selection-breadcrumb">
                  <span className="selected-item">
                    <img 
                      src={getMediumtypeIcon(filters.Mediumtype)} 
                      alt={filters.Mediumtype}
                      className="breadcrumb-icon"
                    />
                    {filters.Mediumtype}
                  </span>
                </div>
                <button className="back-button" onClick={handleStepBack}>
                  &larr; {t('backToMediumtype', 'Back to Mediumtype')}
                </button>
                <div className="options-grid">
                  {getUniqueValues('Titel')
                    .map((value, index) => (
                      <div 
                        key={index} 
                        className="option-card title-card"
                        onClick={() => handleStepSelection(value)}
                      >
                        <div className="title-logo">
                          <img 
                            src={`${process.env.PUBLIC_URL}/assets/brands/${getBrandLogo(value)}`} 
                            alt={value}
                            onError={(e) => {
                              // If this is the first error, try the first word of the title
                              if (!e.target.dataset.tried) {
                                e.target.dataset.tried = '1';
                                const titleWithoutDomain = value?.replace(/\.nl/gi, '');
                                const firstWord = titleWithoutDomain?.toLowerCase().split(' ')[0];
                                if (firstWord) {
                                  e.target.src = `${process.env.PUBLIC_URL}/assets/brands/${firstWord}.svg`;
                                  return;
                                }
                              }
                              // If this is the second error, try the title without spaces
                              else if (e.target.dataset.tried === '1') {
                                e.target.dataset.tried = '2';
                                const titleWithoutDomain = value?.replace(/\.nl/gi, '');
                                const noSpaces = titleWithoutDomain?.toLowerCase().replace(/[^a-z0-9]/g, '');
                                if (noSpaces) {
                                  e.target.src = `${process.env.PUBLIC_URL}/assets/brands/${noSpaces}.svg`;
                                  return;
                                }
                              }
                              
                              // If all attempts fail, use the placeholder
                              e.target.onerror = null;
                              e.target.src = `${process.env.PUBLIC_URL}/assets/brands/brand-placeholder.svg`;
                            }}
                          />
                        </div>
                        <div className="title-label"><span>{value}</span></div>
                      </div>
                    ))}
                </div>
              </div>
            )}
            
            {currentStep === 3 && (
              <div className="step-options">
                <h3>
                  {isPrintMediumType(filters.Mediumtype) ? t('selectAandeel', 'Select Aandeel') : t('selectFormat', 'Select Format')}
                </h3>
                <div className="selection-breadcrumb">
                  <span className="selected-item">
                    <img 
                      src={getMediumtypeIcon(filters.Mediumtype)} 
                      alt={filters.Mediumtype}
                      className="breadcrumb-icon"
                    />
                    {filters.Mediumtype}
                  </span>
                  <span className="breadcrumb-separator">›</span>
                  <span className="selected-item">{filters.Titel}</span>
                </div>
                <button className="back-button" onClick={handleStepBack}>
                  &larr; {t('backToTitel', 'Back to Titel')}
                </button>
                <div className="options-grid">
                                    {(() => {
                    const isPrint = isPrintMediumType(filters.Mediumtype);
                    
                    const values = isPrint ? getUniqueValues('Aandeel') : getUniqueValues('Formaat');
                    
                    return values
                      .sort((a, b) => {
                        // Only sort Aandeel values (print media) by decimal value
                        if (isPrint) {
                          // Convert fractions to decimal for comparison
                          const getDecimalValue = (value) => {
                            if (value.includes('/')) {
                              const [num, denom] = value.split('/');
                              return parseFloat(num) / parseFloat(denom);
                            }
                            return parseFloat(value);
                          };
                          
                          const aValue = getDecimalValue(a);
                          const bValue = getDecimalValue(b);
                          
                          // Sort in descending order (largest first)
                          return bValue - aValue;
                        }
                        // For Formaat values (digital media), don't sort
                        return 0;
                      })
                      .map((value, index) => {
                        // Create a dummy product object for the Aandeel/Formaat
                        // Determine if we're dealing with a print or digital product based on the medium type
                        const isDigitalMedium = filters.Mediumtype === 'Online';
                        
                        const dummyProduct = {
                          Medium: isPrint ? 'Print' : (isDigitalMedium ? 'Online' : ''),
                          Mediumtype: filters.Mediumtype,
                          Aandeel: isPrint ? value : '',
                          Formaat: !isPrint ? value : '',
                          Product: isDigitalMedium ? value : ''  // For digital products, the value might actually be a product name
                        };
                        
                        const { iconPath, placeholderPath, isPrint: isPrintIcon, isDigital } = getProductIcon(dummyProduct);
                        
                        return (
                          <div 
                            key={index} 
                            className={`option-card format-card ${isPrintIcon ? 'print-icon' : ''} ${isDigital ? 'digital-icon' : ''}`}
                            onClick={() => handleStepSelection(value)}
                          >
                            <div className="format-image">
                              <img 
                                src={iconPath ? `${process.env.PUBLIC_URL}${iconPath}` : (placeholderPath ? `${process.env.PUBLIC_URL}${placeholderPath}` : IMAGE_LOAD_STOP_DATA_URI)}
                                alt={value}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  if (e.target.dataset.fallbackUsed) {
                                    e.target.src = IMAGE_LOAD_STOP_DATA_URI;
                                  } else {
                                    e.target.dataset.fallbackUsed = '1';
                                    e.target.src = placeholderPath ? `${process.env.PUBLIC_URL}${placeholderPath}` : IMAGE_LOAD_STOP_DATA_URI;
                                  }
                                }}
                              />
                            </div>
                            <div className="format-label">{value}</div>
                          </div>
                        );
                      });
                  })()}
                </div>
              </div>
            )}
            
            {currentStep === 4 && (
              <div className="step-options">
                <h3>{t('selectPosition', 'Select Position')}</h3>
                <div className="selection-breadcrumb">
                  <span className="selected-item">
                    <img 
                      src={getMediumtypeIcon(filters.Mediumtype)} 
                      alt={filters.Mediumtype}
                      className="breadcrumb-icon"
                    />
                    {filters.Mediumtype}
                  </span>
                  <span className="breadcrumb-separator">›</span>
                  <span className="selected-item">{filters.Titel}</span>
                  <span className="breadcrumb-separator">›</span>
                  <span className="selected-item">
                    {isPrintMediumType(filters.Mediumtype) ? filters.Aandeel : filters.Formaat}
                  </span>
                </div>
                <button className="back-button" onClick={handleStepBack}>
                  &larr; {isPrintMediumType(filters.Mediumtype) ? t('backToAandeel', 'Back to Aandeel') : t('backToFormat', 'Back to Format')}
                </button>
                
                <div className="position-and-results">
                  <div className="position-selection">
                    <div className="options-grid">
                      {getUniqueValues('Positie')
                        .map((value, index) => (
                          <div 
                            key={index} 
                            className="option-card"
                            onClick={() => handleStepSelection(value)}
                          >
                            {value}
                          </div>
                        ))}
                    </div>
                  </div>
                  
                  {!isFiltering && filteredData.length > 0 && (
                    <div className="step-results-container">
                      <h4>{t('results', 'Results')} <span className="results-count">({filteredData.length})</span></h4>
                      {selectionMode === 'region' && (
                        <button className="filter-results-button" onClick={handleFilterResultsClick}>
                          {t('filterResults', 'Filter de resultaten')}
                        </button>
                      )}
                      <div className="step-cards-list">
                        <CardsList 
                          filteredData={filteredData}
                          selectedRow={selectedRow}
                          setSelectedRow={setSelectedRow}
                          getDisplayColumns={getDisplayColumns}
                        />
                      </div>
                      
                      {selectedRow && (
                        <div className="step-details-view" id="product-details-section">
                          <h4>{t('productDetail', 'Product detail')}</h4>
                          <DetailedView 
                            selectedRow={selectedRow}
                            columns={columns}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  
                  {!isFiltering && filteredData.length === 0 && filters.Positie && (
                    <div className="error-message">
                      {t('noResultsFound')}
                    </div>
                  )}
                  
                  {isFiltering && (
                    <div className="loading-container">
                      <LoadingSpinner />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {!showResults && selectionMode === 'filter' && (
        <div className="warning-message">
          {t('selectMediumtypeAndTitel')}
        </div>
      )}

      {!showResults && selectionMode === 'region' && regionStep === 1 && !filters.Zipcode && (
        <div className="warning-message">
          {t('selectProvinceOrZipcode')}
        </div>
      )}

      {!showResults && selectionMode === 'region' && regionStep === 1 && filters.Zipcode && filters.Zipcode.length < 4 && (
        <div className="warning-message">
          {t('enterCompleteZipcode')}
        </div>
      )}

      {!showResults && selectionMode === 'region' && regionStep === 2 && (
        <div className="warning-message">
          {t('selectTitelToContinue', 'Please select a title to see available products')}
        </div>
      )}

      {!showResults && selectionMode === 'region' && regionStep === 3 && (
        <div className="warning-message">
          {t('selectAandeelToContinue', 'Please select a Aandeel to see available products')}
        </div>
      )}

      {showResults && isFiltering && (selectionMode === 'filter' || selectionMode === 'region') && <LoadingSpinner />}

      {showResults && !isFiltering && selectionMode === 'filter' && (
        <div className="container-width-limit">
          <div className="filter-results">
            <div className="results-header">
              <h4>{t('results', 'Results')} <span className="results-count">({filteredData.length})</span></h4>
            </div>
            {/* Content Type Toggle for Print Media */}
            {isPrintMediumType(filters.Mediumtype) && (
              <div className="content-type-toggle">
                <div className="toggle-container">
                  <span className="toggle-label">
                    {t('advertising', 'Advertising')}
                    <span className="content-type-count">({contentTypeCounts.advertising})</span>
                  </span>
                  <label className={`toggle-switch ${(contentTypeCounts.advertising === 0 || contentTypeCounts.branded === 0) ? 'disabled' : ''}`}>
                    <input
                      type="checkbox"
                      checked={contentType === 'branded'}
                      onChange={() => setContentType(contentType === 'advertising' ? 'branded' : 'advertising')}
                      disabled={contentTypeCounts.advertising === 0 || contentTypeCounts.branded === 0}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className="toggle-label">
                    {t('brandedContent', 'Branded Content')}
                    <span className="content-type-count">({contentTypeCounts.branded})</span>
                  </span>
                </div>
              </div>
            )}
            <div className="results-grid">
              <CardsList 
                filteredData={filteredData}
                selectedRow={selectedRow}
                setSelectedRow={setSelectedRow}
                getDisplayColumns={getDisplayColumns}
              />
              <DetailedView 
                selectedRow={selectedRow}
                columns={columns}
                id="product-details-section"
              />
            </div>
          </div>
        </div>
      )}



      {showResults && !isFiltering && filteredData.length === 0 && (
        <div className="error-message">
          {t('noResultsFound')}
        </div>
      )}
    </div>
  );
};

export default CsvViewer;